import type { Client } from '@/types';
import { normalizeString } from './string-utils';
import { nomDuPays } from './pays';

/**
 * NOM AFFICHÉ, NOM D'ORIGINE, NOM CHERCHÉ.
 *
 * Trois questions distinctes, trois fonctions. Elles vivent ici parce qu'une
 * trentaine d'écrans les appellent : la liste des clients, la fiche, les deux
 * éditeurs de facture, les listes de factures, de devis, d'avoirs, la
 * corbeille, les archives et le tableau de bord. Une règle écrite ailleurs
 * qu'ici divergerait d'un écran à l'autre.
 *
 * La version latine est calculée à l'ÉCRITURE et rangée en base ; ces fonctions
 * ne translittèrent rien elles-mêmes. C'est délibéré : romaniser à l'affichage
 * ferait tourner « any-ascii » à chaque rendu de liste, et surtout la recherche
 * et le PDF n'auraient aucune garantie de voir la MÊME chaîne.
 */

/** Le champ latin s'il apporte quelque chose, l'original sinon. */
function latinOuOriginal(
    latin: string | null | undefined,
    original: string | null | undefined
): string {
    const romanise = latin?.trim();
    if (romanise) return romanise;
    return original?.trim() || '';
}

/**
 * Le nom à AFFICHER : latin quand une romanisation existe, original sinon.
 *
 * « sinon » couvre deux situations que l'appelant n'a pas à distinguer : la
 * fiche est déjà latine (le cas courant), ou sa romanisation s'est révélée
 * impossible. Dans les deux cas c'est l'original qu'il faut montrer — jamais
 * une chaîne vide.
 *
 * - Particulier (INDIVIDUAL) : « Prénom Nom »
 * - Société (COMPANY) : la raison sociale
 */
export function getClientDisplayName(client: Client | null | undefined): string {
    if (!client) return '';

    if (client.typeClient === 'INDIVIDUAL') {
        const prenom = latinOuOriginal(client.prenomContactLatin, client.prenomContact);
        const nom = latinOuOriginal(client.nomContactLatin, client.nomContact);

        if (prenom && nom) return `${prenom} ${nom}`;
        if (prenom) return prenom;
        if (nom) return nom;
    }

    return latinOuOriginal(client.nomLatin, client.nom);
}

/**
 * Le nom tel que le client l'écrit LUI-MÊME, dans son écriture.
 *
 * Rendu VIDE lorsqu'il ne diffère pas du nom affiché : l'interface s'en sert
 * pour décider s'il y a une seconde ligne à montrer, et afficher deux fois la
 * même chaîne serait du bruit.
 */
export function getClientOriginalName(client: Client | null | undefined): string {
    if (!client) return '';

    const original =
        client.typeClient === 'INDIVIDUAL'
            ? [client.prenomContact?.trim(), client.nomContact?.trim()].filter(Boolean).join(' ')
            : (client.nom || '').trim();

    return original && original !== getClientDisplayName(client) ? original : '';
}

/**
 * Get initials for a client avatar
 * - For individuals: Uses first letter of first name + first letter of last name
 * - For companies: Uses first two letters of company name
 *
 * Les initiales suivent le nom AFFICHÉ : une pastille portant un idéogramme à
 * côté d'une ligne latine ne se rattacherait visuellement à rien.
 */
export function getClientInitials(client: Client | null | undefined): string {
    if (!client) return '';

    if (client.typeClient === 'INDIVIDUAL') {
        const prenom = latinOuOriginal(client.prenomContactLatin, client.prenomContact);
        const nom = latinOuOriginal(client.nomContactLatin, client.nomContact);

        const premiere = prenom.charAt(0).toUpperCase();
        const seconde = nom.charAt(0).toUpperCase();

        if (premiere && seconde) return `${premiere}${seconde}`;
        if (premiere) return premiere;
        if (seconde) return seconde;
    }

    const nom = latinOuOriginal(client.nomLatin, client.nom);
    if (nom.length >= 2) return nom.substring(0, 2).toUpperCase();
    return nom.charAt(0).toUpperCase();
}

/**
 * L'ADRESSE AFFICHÉE, en version latine, ligne par ligne.
 *
 * Même règle que pour le nom : la version latine calculée à l'écriture quand
 * elle existe, l'original sinon — jamais un vide. Le PAYS y est NOMMÉ plutôt
 * que codé : « UA » n'est pas un pays, c'est un code, et il n'a rien à faire
 * sous les yeux de qui lit une fiche.
 *
 * Rendue sous forme de lignes plutôt que d'une chaîne : la fiche les empile,
 * la liste les joint par une virgule, et c'est à l'appelant de choisir. Les
 * lignes vides sont écartées ici, une fois pour toutes.
 */
export function getClientAddressLines(client: Client | null | undefined): string[] {
    if (!client) return [];

    // LE PAYS EST UN COMPLÉMENT, PAS UNE ADRESSE.
    //
    // Sans voie ni ville, il n'y a rien à afficher — et surtout pas le seul
    // pays. Constaté en préproduction : les fiches dépourvues d'adresse, qui
    // affichaient « - », se sont mises à afficher « France ». On lit alors une
    // adresse là où il n'y en a aucune, ce qui est pire que le tiret.
    const aUneAdresse = !!(
        client.adresse?.trim() || client.adresse2?.trim() || client.ville?.trim()
    );
    if (!aUneAdresse) return [];

    const codePostalEtVille = [
        client.codePostal?.trim(),
        latinOuOriginal(client.villeLatin, client.ville),
    ]
        .filter(Boolean)
        .join(' ');

    return [
        latinOuOriginal(client.adresseLatin, client.adresse),
        latinOuOriginal(client.adresse2Latin, client.adresse2),
        codePostalEtVille,
        nomDuPays(client.pays),
    ].filter(Boolean);
}

/**
 * L'adresse telle que le client l'écrit lui-même, pour la montrer À CÔTÉ de la
 * version latine. Vide quand elle ne dit rien de plus — ce qui est le cas de
 * toutes les fiches françaises.
 */
export function getClientOriginalAddress(client: Client | null | undefined): string[] {
    if (!client) return [];

    const original = [
        client.adresse?.trim(),
        client.adresse2?.trim(),
        [client.codePostal?.trim(), client.ville?.trim()].filter(Boolean).join(' '),
    ].filter(Boolean) as string[];

    const latin = getClientAddressLines(client);
    // La comparaison porte sur les lignes de voie et de ville, pas sur le pays :
    // « UA » devient « Ukraine » même quand l'adresse est déjà latine, et cela
    // ne justifie pas d'afficher une seconde adresse identique.
    const memeChose = original.every((ligne, i) => ligne === latin[i]);
    return memeChose ? [] : original;
}

/**
 * Le texte sur lequel porte la recherche.
 *
 * Il contient les DEUX écritures. C'est la condition pour que « Vezom » et
 * « Везом » ramènent la même fiche : qui a le nom sous les yeux dans un
 * courriel le tapera dans son écriture, qui l'a vu dans la liste le tapera en
 * latin, et aucun des deux ne doit repartir bredouille.
 *
 * Les identifiants — courriel, SIRET, TVA, identifiant Stripe — y figurent
 * aussi : ce sont eux qu'on tape quand le nom est justement le problème.
 */
export function getClientSearchText(client: Client): string {
    const parts = [
        client.nom,
        client.nomLatin,
        client.prenomContact,
        client.prenomContactLatin,
        client.nomContact,
        client.nomContactLatin,
        client.ville,
        client.villeLatin,
        client.email,
        client.siret,
        client.tvaIntra,
        client.stripeCustomerId,
    ].filter(Boolean);

    return normalizeString(parts.join(' '));
}

/**
 * Vrai si la fiche porte une écriture non latine — ce qui vaut à l'interface
 * une seconde ligne, et au PDF une décision de police.
 */
export function aUneEcritureOriginale(client: Client | null | undefined): boolean {
    return !!client?.scriptOriginal && client.scriptOriginal !== 'latin';
}

/**
 * « À VÉRIFIER » — un signalement, jamais un verrou.
 *
 * Vrai quand au moins un champ porte une romanisation incertaine que personne
 * n'a encore relue. Un abjad — arabe, hébreu — n'écrit pas ses voyelles, et
 * aucune table ne les devinera ; un kanji lu par « any-ascii » sort avec sa
 * prononciation chinoise. Ces romanisations sont utilisables, elles ne sont pas
 * sûres, et quelqu'un devrait les regarder.
 *
 * RIEN DANS LA FACTURATION NE DOIT APPELER CETTE FONCTION. Une facture s'émet,
 * s'envoie et s'encaisse à l'identique sur une fiche signalée : un client qu'on
 * ne pourrait pas facturer parce qu'une table de translittération hésite serait
 * un défaut bien pire que celui qu'on corrige. Elle n'existe que pour afficher
 * une pastille et permettre de filtrer.
 */
export function clientAVerifier(client: Client | null | undefined): boolean {
    return (client?.champsAVerifier?.length ?? 0) > 0;
}

/** Les champs dont la version latine a été saisie à la main. */
export function champEstVerrouille(client: Client | null | undefined, champ: string): boolean {
    return !!client?.champsLatinsVerrouilles?.includes(champ);
}
